/**
 * Mock data for the Run Kilometres page.
 *
 * Everything here is fabricated client-side so the screen can be reviewed
 * before the FMS exposes a real odometer / run-distance endpoint. Replace
 * `listVehicleRuns` with an `api.listVehicleRuns()` call when the backend
 * lands — the `VehicleRun` shape below is the contract we want from it.
 *
 * Distance for an in-progress run is NOT stored: it is derived from the
 * elapsed time and the run's average speed (see `runDistanceKm`), so the
 * table counts up in real time exactly the way live GPS accrual would.
 */

export type RunSource = "auto" | "driver";
export type RunStatus = "in_progress" | "completed";

export interface RunLeg {
  seq: number;
  from_stop: string;
  to_stop: string;
  /** Segment length in km. */
  km: number;
  /** Arrival at `to_stop`; null while the bus is still on this leg. */
  arrived_at: string | null;
  dwell_seconds: number | null;
}

export interface VehicleRun {
  id: string;
  service_date: string;
  vehicle_id: string;
  plate: string;
  route_short_name: string;
  route_long_name: string;
  route_color: string;
  driver_name: string;
  driver_employee_id: string;
  /** How the run got opened: GPS geofence auto-detect, or driver tapped Start. */
  source: RunSource;
  status: RunStatus;
  started_at: string;
  /** null while in progress. */
  ended_at: string | null;
  stops_covered: number;
  stops_planned: number;
  /** Vehicle odometer reading when the run opened. */
  odo_start_km: number;
  /** Final distance for a completed run; ignored while in progress. */
  final_km: number;
  /** Average moving speed (km/h) — drives live accrual and the avg-speed column. */
  avg_speed_kph: number;
  /** Minutes spent stationary outside scheduled dwell. */
  idle_minutes: number;
  legs: RunLeg[];
}

// ── Deterministic pseudo-randomness ──────────────────────────────────────
// A fixed seed keeps the mock stable across re-renders and page reloads, so
// the numbers don't jitter every time React repaints.
function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// Orange Line corridor, as it appears in the CDA GTFS feed.
const STATIONS = [
  "Faiz Ahmad Faiz",
  "NHA",
  "G-10",
  "Police Foundation",
  "NUST",
  "G-13",
  "Golra Morr",
  "N-5",
  "Airport",
];

// Inter-station distances (km) along the corridor, Faiz Ahmad Faiz → Airport.
const LEG_KM = [1.9, 2.4, 1.7, 2.2, 1.6, 2.8, 4.1, 8.6];

const DRIVERS = [
  ["Imran Shah", "CDA-1042"],
  ["Adnan Riaz", "CDA-1078"],
  ["Kashif Mehmood", "CDA-1113"],
  ["Waqar Ahmed", "CDA-1156"],
  ["Bilal Hussain", "CDA-1190"],
  ["Sajid Iqbal", "CDA-1204"],
  ["Naveed Akhtar", "CDA-1231"],
  ["Tariq Javed", "CDA-1267"],
];

const PLATES = [
  "ICT-2041", "ICT-2044", "ICT-2052", "ICT-2058",
  "ICT-2063", "ICT-2071", "ICT-2077", "ICT-2085",
];

function iso(d: Date): string {
  return d.toISOString();
}

function buildLegs(
  outbound: boolean,
  stopsCovered: number,
  startMs: number,
  speedKph: number,
  rnd: () => number
): RunLeg[] {
  const names = outbound ? STATIONS : [...STATIONS].reverse();
  const kms = outbound ? LEG_KM : [...LEG_KM].reverse();
  const legs: RunLeg[] = [];
  let t = startMs;
  for (let i = 0; i < kms.length; i++) {
    const km = kms[i];
    const travelMs = (km / speedKph) * 3600_000;
    const dwell = 25 + Math.round(rnd() * 50);
    t += travelMs;
    const reached = i + 1 <= stopsCovered - 1;
    legs.push({
      seq: i + 1,
      from_stop: names[i],
      to_stop: names[i + 1],
      km,
      arrived_at: reached ? iso(new Date(t)) : null,
      dwell_seconds: reached ? dwell : null,
    });
    t += dwell * 1000;
  }
  return legs;
}

function todayIso(d = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * Build the run log for a service date. Runs earlier in the day are
 * completed; a handful straddling "now" are still in progress.
 */
export function listVehicleRuns(serviceDate: string): VehicleRun[] {
  const seed = Array.from(serviceDate).reduce((a, c) => a + c.charCodeAt(0), 7);
  const rnd = mulberry32(seed);

  const day = new Date(`${serviceDate}T00:00:00`);
  const now = Date.now();
  const isToday = serviceDate === todayIso();
  // On a past date the whole service day has already run; on today we only
  // generate runs that would have started by now.
  const cutoff = isToday ? now : day.getTime() + 22 * 3600_000;

  const runs: VehicleRun[] = [];
  const totalStops = STATIONS.length;

  for (let bus = 0; bus < PLATES.length; bus++) {
    // Each bus signs on somewhere in the 05:40–07:10 window, then cycles the
    // corridor end-to-end with a layover between runs.
    let cursor = day.getTime() + (5 * 60 + 40 + Math.round(rnd() * 90)) * 60_000;
    const odoBase = 118_400 + Math.round(rnd() * 46_000);
    let odo = odoBase;
    let idx = 0;

    while (cursor < cutoff && idx < 12) {
      const outbound = idx % 2 === 0;
      const speed = 22 + rnd() * 10; // 22–32 km/h incl. dwell drag
      const fullKm = LEG_KM.reduce((a, b) => a + b, 0);
      const plannedMs = (fullKm / speed) * 3600_000 + totalStops * 40_000;
      const endMs = cursor + plannedMs;
      const inProgress = isToday && endMs > now;

      // Fraction of the run completed so far drives the stop counter.
      const frac = inProgress ? Math.min(1, (now - cursor) / plannedMs) : 1;
      const stopsCovered = inProgress
        ? Math.max(1, Math.min(totalStops, Math.floor(frac * totalStops) + 1))
        : totalStops;

      // ~62% of runs open themselves off the depot-exit geofence; the rest
      // are started by the driver from the console app.
      const source: RunSource = rnd() < 0.62 ? "auto" : "driver";

      runs.push({
        id: `run-${serviceDate}-${bus}-${idx}`,
        service_date: serviceDate,
        vehicle_id: `veh-${bus + 1}`,
        plate: PLATES[bus],
        route_short_name: "Orange Line",
        route_long_name: outbound
          ? "Faiz Ahmad Faiz to Airport"
          : "Airport to Faiz Ahmad Faiz",
        route_color: "FFA500",
        driver_name: DRIVERS[bus][0],
        driver_employee_id: DRIVERS[bus][1],
        source,
        status: inProgress ? "in_progress" : "completed",
        started_at: iso(new Date(cursor)),
        ended_at: inProgress ? null : iso(new Date(endMs)),
        stops_covered: stopsCovered,
        stops_planned: totalStops,
        odo_start_km: Math.round(odo),
        // Deadhead and re-routing put real distance a little over the
        // schedule's shape length.
        final_km: +(fullKm * (1 + rnd() * 0.06)).toFixed(1),
        avg_speed_kph: +speed.toFixed(1),
        idle_minutes: Math.round(rnd() * 9),
        legs: buildLegs(outbound, stopsCovered, cursor, speed, rnd),
      });

      odo += fullKm;
      cursor = endMs + (8 + Math.round(rnd() * 14)) * 60_000; // layover
      idx++;
    }
  }

  return runs.sort(
    (a, b) => new Date(b.started_at).getTime() - new Date(a.started_at).getTime()
  );
}

/**
 * Distance covered by a run as of `now`.
 *
 * Completed runs return their stored total. In-progress runs accrue from
 * elapsed time × average speed, which is what makes the table tick live.
 */
export function runDistanceKm(run: VehicleRun, now: number): number {
  if (run.status === "completed") return run.final_km;
  const elapsedH = Math.max(0, now - new Date(run.started_at).getTime()) / 3600_000;
  return Math.min(run.final_km, +(elapsedH * run.avg_speed_kph).toFixed(2));
}

/** Run duration in seconds, live for in-progress runs. */
export function runDurationSec(run: VehicleRun, now: number): number {
  const start = new Date(run.started_at).getTime();
  const end = run.ended_at ? new Date(run.ended_at).getTime() : now;
  return Math.max(0, Math.round((end - start) / 1000));
}
